/**
 * AI Flow Enhancement Modal
 * Issue #392: AI enhancement of E2E flow diagrams
 *
 * Modal for enhancing E2E flows with AI-suggested improvements.
 * Supports two depth levels: Standard (AST context) and Deep (+ file contents).
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { E2EFlowPath } from '@veodiagram/shared';
import { api } from '../../services/api';
import { PromptEditorModal } from './PromptEditorModal';
import { ContextViewer } from './ContextViewer';

// Issue #417: Type for prompt preview response
interface PromptPreview {
  systemPrompt: string;
  guidelines: string;
  customGuidelines?: string;
  flowContext: string;
  codeContext: {
    apiCalls: string;
    databaseOps: string;
    evidenceSnippets: string;
    fileContents: Array<{
      filePath: string;
      content: string;
      relevance: string;
      size: number;
      included: boolean;
    }>;
  };
  outputInstructions: string;
  verificationPrompt?: string;
  fullPrompt: string;
}

// Evidence level type (Issue #407)
type EvidenceLevel = 'strong' | 'partial' | 'inferred';

// Issue #436: Missing step category type
type MissingStepCategory = 'missed' | 'suggested';

// Code reference for missed steps (Issue #436)
interface CodeReference {
  functionName?: string;
  variableName?: string;
  apiEndpoint?: string;
  codeSnippet?: string;
}

// Evidence source type
interface EvidenceSource {
  type: 'file' | 'snippet' | 'pattern' | 'ast';
  filePath?: string;
  lineStart?: number;
  lineEnd?: number;
  description: string;
}

// Issue #411: Verification question detail
interface VerificationDetail {
  question: string;
  questionType: string;
  answer: string;
  confidence: number;
}

// Issue #411: What changed during verification
interface VerificationRevision {
  type: 'name' | 'description' | 'missingStepRemoved' | 'missingStepAdded' | 'stepLabelChanged';
  original?: string;
  revised?: string;
  reason: string;
}

// Enhancement result type from API
interface EnhancementResult {
  flowId: string;
  depth: 'standard' | 'deep' | 'ultra-deep';
  suggestedName?: string;
  originalName?: string;
  suggestedDescription?: string;
  originalDescription?: string;
  stepEnhancements: Array<{
    stepId: string;
    originalLabel?: string;
    suggestedLabel: string;
    originalDescription?: string;
    suggestedDescription?: string;
    reasoning: string;
    confidence: number;
    evidenceLevel?: EvidenceLevel;
    evidenceSources?: EvidenceSource[];
  }>;
  missingSteps: Array<{
    afterStepId: string;
    suggestedType: string;
    suggestedName: string;
    description: string;
    reasoning: string;
    confidence: number;
    evidenceLevel?: EvidenceLevel;
    evidenceSources?: EvidenceSource[];
    category?: MissingStepCategory; // Issue #436
    codeReference?: CodeReference; // Issue #436
  }>;
  completeness: {
    score: number;
    reasoning: string;
    suggestions: string[];
    gaps?: string[];
  };
  enhancements: Array<{
    type: 'name' | 'description' | 'stepLabel' | 'missingStep' | 'completeness';
    stepId?: string;
    original?: string;
    suggested: string;
    reasoning: string;
    confidence: number;
    accepted?: boolean;
    evidenceLevel?: EvidenceLevel;
    evidenceSources?: EvidenceSource[];
  }>;
  tokensUsed?: number;
  durationMs?: number;
  cached?: boolean;
  /** Issue #411: Whether AI verification loop was used */
  verificationUsed?: boolean;
  /** Issue #411: Number of verification questions asked */
  questionsAsked?: number;
  /** Issue #411: Details of verification questions and answers */
  verificationDetails?: VerificationDetail[];
  /** Issue #411: What changed between Phase 1 and Phase 2 */
  verificationRevisions?: VerificationRevision[];
}

// Enhancement item with acceptance state
interface EnhancementItem {
  id: string;
  type: 'name' | 'description' | 'stepLabel' | 'missingStep';
  stepId?: string;
  original?: string;
  suggested: string;
  reasoning: string;
  confidence: number;
  accepted: boolean | null; // null = pending, true = accepted, false = rejected
  evidenceLevel?: EvidenceLevel;
  evidenceSources?: EvidenceSource[];
  category?: MissingStepCategory; // Issue #436: For missingStep type
  codeReference?: CodeReference; // Issue #436: For missed steps
}

// Evidence level badge configuration
const EVIDENCE_LEVEL_CONFIG: Record<EvidenceLevel, { label: string; color: string; icon: string; tooltip: string }> = {
  strong: {
    label: 'Strong',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: '✓',
    tooltip: 'Based on actual code analysis (file contents, evidence snippets)',
  },
  partial: {
    label: 'Partial',
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: '◐',
    tooltip: 'Based on detected patterns (AST, API calls, DB queries)',
  },
  inferred: {
    label: 'Inferred',
    color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
    icon: '?',
    tooltip: 'AI reasoning without direct code evidence',
  },
};

// Issue #436: Missing step category badge configuration
const MISSING_STEP_CATEGORY_CONFIG: Record<MissingStepCategory, { label: string; color: string; icon: string; tooltip: string }> = {
  missed: {
    label: 'Missed Step',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    icon: '⚠',
    tooltip: 'Step exists in code but was not detected by analysis',
  },
  suggested: {
    label: 'Suggestion',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: '💡',
    tooltip: 'AI recommendation - code does not exist yet',
  },
};

// Issue #411: Enhancement result passed to parent
export interface EnhancementCompletionInfo {
  depth: 'standard' | 'deep' | 'ultra-deep';
  acceptedCount: number;
  rejectedCount: number;
  verificationUsed: boolean;
  questionsAsked: number;
  verificationDetails: VerificationDetail[];
  verificationRevisions: VerificationRevision[];
  enhancedAt: string;
  /** Issue #424: Full prompt sent to AI */
  promptSent?: string;
  /** Issue #424: Raw AI response */
  aiResponse?: string;
}

interface AIFlowEnhancementModalProps {
  projectId: string;
  flow: E2EFlowPath;
  /** Called when enhancement is complete. Optional parameter includes enhancement details for display. */
  onComplete: (info?: EnhancementCompletionInfo) => void;
  onClose: () => void;
}

type ModalPhase = 'configure' | 'enhancing' | 'review' | 'applying';

export function AIFlowEnhancementModal({
  projectId,
  flow,
  onComplete,
  onClose,
}: AIFlowEnhancementModalProps) {
  const [phase, setPhase] = useState<ModalPhase>('configure');
  const [depth, setDepth] = useState<'standard' | 'deep' | 'ultra-deep'>('standard');
  // Issue #411: Enable AI verification loop
  const [enableVerification, setEnableVerification] = useState(false);
  const [enhancements, setEnhancements] = useState<EnhancementItem[]>([]);
  const [completeness, setCompleteness] = useState<EnhancementResult['completeness'] | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [applyProgress, setApplyProgress] = useState({ current: 0, total: 0 });
  // Issue #411: Store verification results
  const [verificationResult, setVerificationResult] = useState<{
    used: boolean;
    questionsAsked: number;
    details: VerificationDetail[];
    revisions: VerificationRevision[];
  } | null>(null);
  // Issue #424: Store AI debug info (prompt and response)
  const [aiDebugInfo, setAiDebugInfo] = useState<{
    promptSent?: string;
    aiResponse?: string;
    tokensUsed?: number;
    durationMs?: number;
    cached?: boolean;
  } | null>(null);
  const [showAiDebug, setShowAiDebug] = useState(false);
  const abortRef = useRef(false);

  // Issue #417: Advanced options state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [promptPreview, setPromptPreview] = useState<PromptPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [excludedFiles, setExcludedFiles] = useState<Set<string>>(new Set());
  const [customGuidelines, setCustomGuidelines] = useState('');

  // Issue #436: Filter state for missing step categories
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'missed' | 'suggested' | 'other'>('all');
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  // Additional files added by user
  const [additionalFiles, setAdditionalFiles] = useState<Array<{ filePath: string; content: string; size: number }>>([]);

  // Get unique file paths for deep analysis preview
  const getUniqueFilePaths = useCallback(() => {
    const paths = new Set<string>();
    for (const step of flow.steps) {
      if (step.sourceFile) paths.add(step.sourceFile);
      for (const evidence of step.evidence || []) {
        if (evidence.filePath) paths.add(evidence.filePath);
      }
    }
    return Array.from(paths);
  }, [flow]);

  // Issue #417: Load prompt preview when advanced options are shown
  const loadPromptPreview = useCallback(async () => {
    if (!showAdvanced) return;

    setIsLoadingPreview(true);
    try {
      const preview = await api.previewE2EFlowEnhancement(projectId, flow.id, {
        depth,
        enableVerification,
        customGuidelines: customGuidelines || undefined,
        excludeFiles: Array.from(excludedFiles),
      });
      setPromptPreview(preview);
    } catch (err) {
      console.error('Failed to load prompt preview:', err);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [showAdvanced, projectId, flow.id, depth, enableVerification, customGuidelines, excludedFiles]);

  // Load preview when advanced is opened or options change
  useEffect(() => {
    if (showAdvanced) {
      loadPromptPreview();
    }
  }, [showAdvanced, depth, enableVerification]); // Don't include customGuidelines/excludedFiles to avoid re-fetching on every change

  // Issue #417: File toggle handlers for ContextViewer
  const handleToggleFile = useCallback((filePath: string) => {
    setExcludedFiles(prev => {
      const next = new Set(prev);
      if (next.has(filePath)) {
        next.delete(filePath);
      } else {
        next.add(filePath);
      }
      return next;
    });
  }, []);

  const handleSelectAllFiles = useCallback(() => {
    setExcludedFiles(new Set());
  }, []);

  const handleDeselectAllFiles = useCallback(() => {
    if (promptPreview?.codeContext.fileContents) {
      setExcludedFiles(new Set(promptPreview.codeContext.fileContents.map(f => f.filePath)));
    }
  }, [promptPreview]);

  // Issue #417: Handle prompt editor save
  const handlePromptSave = useCallback((_customPrompt: string | null, newGuidelines: string | null) => {
    if (newGuidelines !== null) {
      setCustomGuidelines(newGuidelines);
    }
    // Note: _customPrompt override is not yet supported in the API, but UI is ready for it
  }, []);

  // Issue #417: Handle adding additional files
  const handleAddFile = useCallback(async (file: { filePath: string; content: string; size: number }) => {
    // Check if file already exists
    if (additionalFiles.some(f => f.filePath === file.filePath)) {
      return;
    }

    try {
      // Load file content via API
      const response = await api.loadFileContent(projectId, file.filePath);
      if (response.content) {
        setAdditionalFiles(prev => [...prev, {
          filePath: file.filePath,
          content: response.content,
          size: response.content.length,
        }]);
      }
    } catch (err) {
      console.error('Failed to load file:', err);
      // Still add the file but without content (will show error in UI)
      setAdditionalFiles(prev => [...prev, {
        filePath: file.filePath,
        content: `// Error: Could not load file content\n// ${err instanceof Error ? err.message : 'Unknown error'}`,
        size: 0,
      }]);
    }
  }, [projectId, additionalFiles]);

  // Issue #417: Handle removing additional files
  const handleRemoveFile = useCallback((filePath: string) => {
    setAdditionalFiles(prev => prev.filter(f => f.filePath !== filePath));
  }, []);

  // Start enhancement
  const startEnhancement = useCallback(async () => {
    setPhase('enhancing');
    setProgress(0);
    setError(null);
    setVerificationResult(null);
    abortRef.current = false;

    // Simulate progress (API doesn't support streaming progress)
    // Issue #411: Verification takes longer, so adjust timing
    const progressInterval = setInterval(() => {
      setProgress(p => Math.min(p + (enableVerification ? 3 : 5), 90));
    }, 500);

    try {
      const result = await api.enhanceE2EFlow(projectId, flow.id, {
        depth,
        enableVerification,
        // Issue #417: Pass advanced options if configured
        customGuidelines: customGuidelines || undefined,
        excludeFiles: excludedFiles.size > 0 ? Array.from(excludedFiles) : undefined,
        additionalFiles: additionalFiles.length > 0 ? additionalFiles : undefined,
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (abortRef.current) return;

      // Convert enhancements to items with acceptance state (including evidence levels)
      const items: EnhancementItem[] = [];
      let idCounter = 0;

      // Find evidence level from flat enhancements list
      const getEvidenceFromEnhancements = (type: string, stepId?: string) => {
        const enhancement = result.enhancements.find(e =>
          e.type === type && (stepId ? e.stepId === stepId : true)
        );
        return {
          evidenceLevel: enhancement?.evidenceLevel,
          evidenceSources: enhancement?.evidenceSources,
        };
      };

      // Add name enhancement
      if (result.suggestedName && result.suggestedName !== result.originalName) {
        const evidence = getEvidenceFromEnhancements('name');
        items.push({
          id: `enhancement-${idCounter++}`,
          type: 'name',
          original: result.originalName,
          suggested: result.suggestedName,
          reasoning: 'AI-suggested user-friendly name',
          confidence: 0.8,
          accepted: null,
          evidenceLevel: evidence.evidenceLevel,
          evidenceSources: evidence.evidenceSources,
        });
      }

      // Add description enhancement
      if (result.suggestedDescription && result.suggestedDescription !== result.originalDescription) {
        const evidence = getEvidenceFromEnhancements('description');
        items.push({
          id: `enhancement-${idCounter++}`,
          type: 'description',
          original: result.originalDescription || '',
          suggested: result.suggestedDescription,
          reasoning: 'AI-suggested description',
          confidence: 0.8,
          accepted: null,
          evidenceLevel: evidence.evidenceLevel,
          evidenceSources: evidence.evidenceSources,
        });
      }

      // Add step enhancements
      for (const step of result.stepEnhancements) {
        if (step.suggestedLabel && step.suggestedLabel !== step.originalLabel) {
          items.push({
            id: `enhancement-${idCounter++}`,
            type: 'stepLabel',
            stepId: step.stepId,
            original: step.originalLabel,
            suggested: step.suggestedLabel,
            reasoning: step.reasoning,
            confidence: step.confidence,
            accepted: null,
            evidenceLevel: step.evidenceLevel,
            evidenceSources: step.evidenceSources,
          });
        }
      }

      // Add missing steps
      for (const missing of result.missingSteps) {
        items.push({
          id: `enhancement-${idCounter++}`,
          type: 'missingStep',
          stepId: missing.afterStepId,
          suggested: missing.suggestedName,
          reasoning: missing.reasoning,
          confidence: missing.confidence,
          accepted: null,
          evidenceLevel: missing.evidenceLevel,
          evidenceSources: missing.evidenceSources,
          category: missing.category, // Issue #436
          codeReference: missing.codeReference, // Issue #436
        });
      }

      setEnhancements(items);
      setCompleteness(result.completeness);

      // Issue #411: Store verification results if available
      if (result.verificationUsed) {
        setVerificationResult({
          used: result.verificationUsed,
          questionsAsked: result.questionsAsked || 0,
          details: result.verificationDetails || [],
          revisions: result.verificationRevisions || [],
        });
      }

      // Issue #424: Store AI debug info (prompt and response)
      setAiDebugInfo({
        promptSent: result.promptSent,
        aiResponse: result.aiResponse,
        tokensUsed: result.tokensUsed,
        durationMs: result.durationMs,
        cached: result.cached,
      });

      setPhase('review');
    } catch (err) {
      clearInterval(progressInterval);
      const errorMessage = err instanceof Error ? err.message : 'Enhancement failed';
      setError(errorMessage);
      setPhase('configure');
    }
  }, [projectId, flow.id, depth, enableVerification]);

  // Accept/reject enhancement
  const handleAccept = useCallback((id: string) => {
    setEnhancements(prev => prev.map(e =>
      e.id === id ? { ...e, accepted: true } : e
    ));
  }, []);

  const handleReject = useCallback((id: string) => {
    setEnhancements(prev => prev.map(e =>
      e.id === id ? { ...e, accepted: false } : e
    ));
  }, []);

  const handleAcceptAll = useCallback(() => {
    setEnhancements(prev => prev.map(e => ({ ...e, accepted: true })));
  }, []);

  const handleRejectAll = useCallback(() => {
    setEnhancements(prev => prev.map(e => ({ ...e, accepted: false })));
  }, []);

  // Apply accepted enhancements
  const applyEnhancements = useCallback(async () => {
    const accepted = enhancements.filter(e => e.accepted === true);
    const rejected = enhancements.filter(e => e.accepted === false);

    // Issue #411: Build enhancement completion info
    const completionInfo: EnhancementCompletionInfo = {
      depth,
      acceptedCount: accepted.length,
      rejectedCount: rejected.length,
      verificationUsed: verificationResult?.used || false,
      questionsAsked: verificationResult?.questionsAsked || 0,
      verificationDetails: verificationResult?.details || [],
      verificationRevisions: verificationResult?.revisions || [],
      enhancedAt: new Date().toISOString(),
      // Issue #424: Include prompt/response for history viewing
      promptSent: aiDebugInfo?.promptSent,
      aiResponse: aiDebugInfo?.aiResponse,
    };

    if (accepted.length === 0) {
      onComplete(completionInfo);
      return;
    }

    setPhase('applying');
    setApplyProgress({ current: 0, total: 1 });

    try {
      // Prepare the data for the API
      const flowName = accepted.find(e => e.type === 'name')?.suggested;
      const flowDescription = accepted.find(e => e.type === 'description')?.suggested;

      const stepEnhancements = accepted
        .filter(e => e.type === 'stepLabel')
        .map(e => ({
          stepId: e.stepId!,
          suggestedLabel: e.suggested,
        }));

      const missingSteps = accepted
        .filter(e => e.type === 'missingStep')
        .map(e => ({
          afterStepId: e.stepId!,
          suggestedType: 'state-change', // Default type
          suggestedName: e.suggested,
          description: e.reasoning,
        }));

      await api.applyE2EFlowEnhancements(projectId, flow.id, {
        flowName,
        flowDescription,
        stepEnhancements: stepEnhancements.length > 0 ? stepEnhancements : undefined,
        missingSteps: missingSteps.length > 0 ? missingSteps : undefined,
      });

      setApplyProgress({ current: 1, total: 1 });

      // Short delay before closing
      setTimeout(() => {
        onComplete(completionInfo);
      }, 500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to apply enhancements';
      setError(errorMessage);
      setPhase('review');
    }
  }, [enhancements, projectId, flow.id, onComplete, depth, verificationResult, aiDebugInfo]);

  // Count pending/accepted/rejected
  const counts = {
    pending: enhancements.filter(e => e.accepted === null).length,
    accepted: enhancements.filter(e => e.accepted === true).length,
    rejected: enhancements.filter(e => e.accepted === false).length,
  };

  const filePaths = getUniqueFilePaths();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-2xl">✨</span>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {phase === 'configure' && 'Enhance Flow with AI'}
              {phase === 'enhancing' && 'Analyzing Flow...'}
              {phase === 'review' && 'Review Suggestions'}
              {phase === 'applying' && 'Applying Changes...'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Configure Phase */}
          {phase === 'configure' && (
            <div className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              <p className="text-slate-600 dark:text-slate-400">
                Choose analysis depth for enhancing "<span className="font-medium">{flow.name}</span>"
              </p>

              <div className="grid grid-cols-3 gap-3">
                {/* Standard option */}
                <button
                  onClick={() => setDepth('standard')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    depth === 'standard'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-slate-200 dark:border-slate-600 hover:border-purple-300'
                  }`}
                >
                  <div className="font-semibold flex items-center gap-2 text-slate-900 dark:text-white text-sm">
                    <span>⚡</span> Standard
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    AST context (~5 sec)
                  </p>
                  <ul className="text-xs text-slate-400 mt-2 space-y-0.5">
                    <li>• Step names/types</li>
                    <li>• API calls</li>
                    <li>• DB operations</li>
                  </ul>
                </button>

                {/* Deep option */}
                <button
                  onClick={() => setDepth('deep')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    depth === 'deep'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-slate-200 dark:border-slate-600 hover:border-purple-300'
                  }`}
                >
                  <div className="font-semibold flex items-center gap-2 text-slate-900 dark:text-white text-sm">
                    <span>🔬</span> Deep
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    + Source files (~15 sec)
                  </p>
                  <ul className="text-xs text-slate-400 mt-2 space-y-0.5">
                    <li>• Handler code</li>
                    <li>• Component files</li>
                    <li>• Route files</li>
                  </ul>
                </button>

                {/* Ultra-Deep option (Issue #426 Phase 3) */}
                <button
                  onClick={() => setDepth('ultra-deep')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    depth === 'ultra-deep'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-slate-200 dark:border-slate-600 hover:border-purple-300'
                  }`}
                >
                  <div className="font-semibold flex items-center gap-2 text-slate-900 dark:text-white text-sm">
                    <span>🧬</span> Ultra-Deep
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    + Call graph (~30 sec)
                  </p>
                  <ul className="text-xs text-slate-400 mt-2 space-y-0.5">
                    <li>• Function signatures</li>
                    <li>• Type definitions</li>
                    <li>• Data transforms</li>
                  </ul>
                </button>
              </div>

              {/* Issue #411: AI Verification Toggle */}
              <div className="mt-4">
                <button
                  onClick={() => setEnableVerification(!enableVerification)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    enableVerification
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                      : 'border-slate-200 dark:border-slate-600 hover:border-amber-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🔍</span>
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-white">
                          AI Verification Loop
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Cross-checks AI suggestions against source code (+10-20 sec)
                        </p>
                      </div>
                    </div>
                    <div className={`w-12 h-6 rounded-full transition-colors ${
                      enableVerification ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}>
                      <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform mt-0.5 ${
                        enableVerification ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </div>
                  </div>
                  <ul className="text-xs text-slate-400 mt-2 space-y-1 ml-8">
                    <li>• Verifies state persistence claims</li>
                    <li>• Traces prop callbacks to parent handlers</li>
                    <li>• Checks API route implementations</li>
                    <li>• Higher accuracy, longer analysis time</li>
                  </ul>
                </button>
              </div>

              {/* Issue #417: Advanced Options - View/Edit Prompt & Context */}
              <div className="mt-4">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400"
                >
                  <span className={`transition-transform ${showAdvanced ? 'rotate-90' : ''}`}>▶</span>
                  <span>Advanced Options</span>
                  {(customGuidelines || excludedFiles.size > 0) && (
                    <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                      Custom
                    </span>
                  )}
                </button>

                {showAdvanced && (
                  <div className="mt-3 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg space-y-4">
                    {/* Loading state */}
                    {isLoadingPreview && (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full" />
                        <span className="ml-2 text-sm text-slate-500">Loading preview...</span>
                      </div>
                    )}

                    {/* Prompt & Context when loaded */}
                    {promptPreview && !isLoadingPreview && (
                      <>
                        {/* Edit Prompt Button */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              AI Prompt & Guidelines
                            </h4>
                            <p className="text-xs text-slate-500">
                              View system prompt and add custom instructions
                            </p>
                          </div>
                          <button
                            onClick={() => setShowPromptEditor(true)}
                            className="px-3 py-1.5 text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900"
                          >
                            Edit Prompt
                          </button>
                        </div>

                        {/* Custom Guidelines Preview */}
                        {customGuidelines && (
                          <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-200 dark:border-purple-800">
                            <div className="text-xs font-medium text-purple-700 dark:text-purple-400 mb-1">
                              Custom Guidelines:
                            </div>
                            <div className="text-xs text-purple-600 dark:text-purple-300 whitespace-pre-wrap">
                              {customGuidelines}
                            </div>
                          </div>
                        )}

                        {/* Context Viewer */}
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Context Data
                          </h4>
                          <ContextViewer
                            flowContext={promptPreview.flowContext}
                            codeContext={promptPreview.codeContext}
                            excludedFiles={excludedFiles}
                            onToggleFile={handleToggleFile}
                            onSelectAll={handleSelectAllFiles}
                            onDeselectAll={handleDeselectAllFiles}
                            additionalFiles={additionalFiles}
                            onAddFile={handleAddFile}
                            onRemoveFile={handleRemoveFile}
                          />
                        </div>

                        {/* Refresh Preview Button */}
                        <div className="flex justify-end">
                          <button
                            onClick={loadPromptPreview}
                            className="text-xs text-slate-500 hover:text-purple-600 flex items-center gap-1"
                          >
                            <span>↻</span> Refresh Preview
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Files preview for deep (only shown when advanced is closed) */}
              {depth === 'deep' && filePaths.length > 0 && !showAdvanced && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                    Files to include ({filePaths.length} files):
                  </p>
                  <div className="text-xs text-slate-500 space-y-1 max-h-24 overflow-auto">
                    {filePaths.slice(0, 5).map(path => (
                      <div key={path} className="truncate">📄 {path}</div>
                    ))}
                    {filePaths.length > 5 && (
                      <div className="text-slate-400">... and {filePaths.length - 5} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Flow info */}
              <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Flow Details:
                </p>
                <div className="text-xs text-slate-500 space-y-1">
                  <div>Steps: {flow.steps.length}</div>
                  <div>Trigger: {flow.triggerType}</div>
                  <div>Complete: {flow.complete ? 'Yes' : 'No'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Enhancing Phase */}
          {phase === 'enhancing' && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-full max-w-xs">
                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      enableVerification ? 'bg-amber-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-center text-sm text-slate-500 mt-2">
                  Analyzing with {depth === 'deep' ? 'deep' : 'standard'} context...
                </p>
                {enableVerification && (
                  <p className="text-center text-xs text-amber-600 dark:text-amber-400 mt-1">
                    🔍 Verification loop active - cross-checking with source code
                  </p>
                )}
              </div>
              <button
                onClick={() => { abortRef.current = true; setPhase('configure'); }}
                className="mt-4 text-sm text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Review Phase */}
          {phase === 'review' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {enhancements.length} suggestion{enhancements.length !== 1 ? 's' : ''} found
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handleAcceptAll}
                    className="text-xs text-green-600 hover:text-green-700"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    Reject All
                  </button>
                </div>
              </div>

              {/* Evidence Level Legend (Issue #407) */}
              <div className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg text-xs">
                <span className="text-slate-500 font-medium">Evidence:</span>
                {(['strong', 'partial', 'inferred'] as EvidenceLevel[]).map(level => (
                  <span
                    key={level}
                    className={`px-2 py-0.5 rounded flex items-center gap-1 ${EVIDENCE_LEVEL_CONFIG[level].color}`}
                    title={EVIDENCE_LEVEL_CONFIG[level].tooltip}
                  >
                    <span>{EVIDENCE_LEVEL_CONFIG[level].icon}</span>
                    {EVIDENCE_LEVEL_CONFIG[level].label}
                  </span>
                ))}
              </div>

              {/* Issue #436: Category Filter */}
              {(() => {
                const missedCount = enhancements.filter(e => e.type === 'missingStep' && e.category === 'missed').length;
                const suggestedCount = enhancements.filter(e => e.type === 'missingStep' && e.category === 'suggested').length;
                const otherCount = enhancements.filter(e => e.type !== 'missingStep').length;
                const hasMissingSteps = missedCount > 0 || suggestedCount > 0;

                if (!hasMissingSteps) return null;

                return (
                  <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg text-xs">
                    <span className="text-slate-500 font-medium">Filter:</span>
                    <button
                      onClick={() => setCategoryFilter('all')}
                      className={`px-2 py-1 rounded transition-colors ${
                        categoryFilter === 'all'
                          ? 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
                          : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      All ({enhancements.length})
                    </button>
                    {missedCount > 0 && (
                      <button
                        onClick={() => setCategoryFilter('missed')}
                        className={`px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                          categoryFilter === 'missed'
                            ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300'
                            : 'text-slate-500 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                        }`}
                      >
                        <span>⚠</span> Missed ({missedCount})
                      </button>
                    )}
                    {suggestedCount > 0 && (
                      <button
                        onClick={() => setCategoryFilter('suggested')}
                        className={`px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                          categoryFilter === 'suggested'
                            ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                            : 'text-slate-500 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                        }`}
                      >
                        <span>💡</span> Suggestions ({suggestedCount})
                      </button>
                    )}
                    {otherCount > 0 && (
                      <button
                        onClick={() => setCategoryFilter('other')}
                        className={`px-2 py-1 rounded transition-colors ${
                          categoryFilter === 'other'
                            ? 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
                            : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        Other ({otherCount})
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Enhancement list */}
              <div className="space-y-3 max-h-64 overflow-auto">
                {enhancements
                  // Issue #436: Apply category filter
                  .filter((item) => {
                    if (categoryFilter === 'all') return true;
                    if (categoryFilter === 'missed') return item.type === 'missingStep' && item.category === 'missed';
                    if (categoryFilter === 'suggested') return item.type === 'missingStep' && item.category === 'suggested';
                    if (categoryFilter === 'other') return item.type !== 'missingStep';
                    return true;
                  })
                  .map((item) => {
                  const evidenceConfig = item.evidenceLevel
                    ? EVIDENCE_LEVEL_CONFIG[item.evidenceLevel]
                    : null;
                  // Issue #436: Get category config for missing steps
                  const categoryConfig = item.type === 'missingStep' && item.category
                    ? MISSING_STEP_CATEGORY_CONFIG[item.category]
                    : null;

                  return (
                    <div
                      key={item.id}
                      className={`p-3 rounded-lg border ${
                        item.accepted === true
                          ? 'border-green-300 bg-green-50 dark:bg-green-900/20'
                          : item.accepted === false
                          ? 'border-red-300 bg-red-50 dark:bg-red-900/20 opacity-50'
                          : 'border-slate-200 dark:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {/* Issue #436: Show category-specific badge for missing steps */}
                            {item.type === 'missingStep' && categoryConfig ? (
                              <span
                                className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${categoryConfig.color}`}
                                title={categoryConfig.tooltip}
                              >
                                <span>{categoryConfig.icon}</span>
                                {categoryConfig.label}
                              </span>
                            ) : (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                item.type === 'name' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                item.type === 'description' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                item.type === 'stepLabel' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400'
                              }`}>
                                {item.type === 'name' ? 'Flow Name' :
                                 item.type === 'description' ? 'Description' :
                                 item.type === 'stepLabel' ? 'Step Label' :
                                 'Missing Step'}
                              </span>
                            )}
                            {/* Evidence Level Badge (Issue #407) */}
                            {evidenceConfig && (
                              <span
                                className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${evidenceConfig.color}`}
                                title={evidenceConfig.tooltip}
                              >
                                <span>{evidenceConfig.icon}</span>
                                {evidenceConfig.label}
                              </span>
                            )}
                            <span className="text-xs text-slate-400">
                              {Math.round(item.confidence * 100)}%
                            </span>
                          </div>
                          {item.original && (
                            <div className="text-xs text-slate-400 line-through mb-1">
                              {item.original}
                            </div>
                          )}
                          <div className="text-sm font-medium text-slate-900 dark:text-white">
                            {item.suggested}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {item.reasoning}
                          </div>
                          {/* Issue #436: Code Reference for missed steps */}
                          {item.category === 'missed' && item.codeReference && (
                            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-600">
                              <div className="text-xs text-slate-400 mb-1">Code reference (exists in codebase):</div>
                              <div className="text-xs text-slate-500 space-y-0.5 bg-slate-100 dark:bg-slate-800 p-2 rounded font-mono">
                                {item.codeReference.functionName && (
                                  <div><span className="text-amber-600">function:</span> {item.codeReference.functionName}</div>
                                )}
                                {item.codeReference.variableName && (
                                  <div><span className="text-amber-600">variable:</span> {item.codeReference.variableName}</div>
                                )}
                                {item.codeReference.apiEndpoint && (
                                  <div><span className="text-amber-600">endpoint:</span> {item.codeReference.apiEndpoint}</div>
                                )}
                                {item.codeReference.codeSnippet && (
                                  <div className="mt-1 text-xs whitespace-pre-wrap overflow-auto max-h-20">
                                    {item.codeReference.codeSnippet}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Evidence Sources (Issue #407) */}
                          {item.evidenceSources && item.evidenceSources.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-600">
                              <div className="text-xs text-slate-400 mb-1">Evidence sources:</div>
                              <ul className="text-xs text-slate-500 space-y-0.5">
                                {item.evidenceSources.slice(0, 3).map((source, i) => (
                                  <li key={i} className="flex items-center gap-1">
                                    <span className="text-slate-400">
                                      {source.type === 'file' ? '📄' :
                                       source.type === 'snippet' ? '✂️' :
                                       source.type === 'pattern' ? '🔍' : '🌳'}
                                    </span>
                                    <span className="truncate">{source.description}</span>
                                  </li>
                                ))}
                                {item.evidenceSources.length > 3 && (
                                  <li className="text-slate-400">
                                    ... and {item.evidenceSources.length - 3} more
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 ml-2">
                          <button
                            onClick={() => handleAccept(item.id)}
                            className={`p-1.5 rounded ${
                              item.accepted === true
                                ? 'bg-green-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-green-100 hover:text-green-600'
                            }`}
                            title="Accept"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => handleReject(item.id)}
                            className={`p-1.5 rounded ${
                              item.accepted === false
                                ? 'bg-red-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-red-100 hover:text-red-600'
                            }`}
                            title="Reject"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Completeness score */}
              {completeness && (
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-slate-700 dark:text-slate-300">Flow Completeness</span>
                    <span className="text-lg font-bold text-purple-600">
                      {Math.round(completeness.score)}%
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mb-2">
                    {completeness.reasoning}
                  </p>
                  {completeness.suggestions.length > 0 && (
                    <div className="text-xs text-slate-400">
                      <strong>Suggestions:</strong>
                      <ul className="list-disc list-inside mt-1">
                        {completeness.suggestions.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Issue #411: AI Verification Results */}
              {verificationResult && verificationResult.used && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">🔍</span>
                    <span className="font-medium text-amber-800 dark:text-amber-300">
                      AI Verification Loop
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded-full">
                      {verificationResult.questionsAsked} question{verificationResult.questionsAsked !== 1 ? 's' : ''} asked
                    </span>
                  </div>
                  <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">
                    AI cross-checked its suggestions against your source code for higher accuracy.
                  </p>
                  {verificationResult.details.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-32 overflow-auto">
                      <div className="text-xs font-medium text-amber-700 dark:text-amber-400">
                        Verification Questions:
                      </div>
                      {verificationResult.details.map((detail, i) => (
                        <div
                          key={i}
                          className="p-2 bg-white dark:bg-slate-800 rounded border border-amber-200 dark:border-amber-700"
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-amber-500 font-medium text-xs">Q{i + 1}:</span>
                            <div className="flex-1">
                              <div className="text-xs text-slate-700 dark:text-slate-300">
                                {detail.question}
                              </div>
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                <span className="font-medium">Answer:</span> {detail.answer}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">
                                  {detail.questionType}
                                </span>
                                <span className="text-[10px] text-slate-400">
                                  {Math.round(detail.confidence * 100)}% confidence
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Issue #411: Show what changed during verification */}
                  {verificationResult.revisions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-orange-500">✏️</span>
                        <span className="text-xs font-medium text-orange-700 dark:text-orange-400">
                          AI Revised {verificationResult.revisions.length} suggestion{verificationResult.revisions.length !== 1 ? 's' : ''} based on verification
                        </span>
                      </div>
                      <div className="space-y-2 max-h-32 overflow-auto">
                        {verificationResult.revisions.map((revision, i) => (
                          <div
                            key={i}
                            className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded border border-orange-200 dark:border-orange-700"
                          >
                            <div className="flex items-start gap-2">
                              <span className={`text-xs font-medium mt-0.5 ${
                                revision.type === 'missingStepRemoved' ? 'text-red-500' :
                                revision.type === 'missingStepAdded' ? 'text-green-500' :
                                'text-orange-500'
                              }`}>
                                {revision.type === 'missingStepRemoved' ? '−' :
                                 revision.type === 'missingStepAdded' ? '+' : '~'}
                              </span>
                              <div className="flex-1">
                                <div className="text-xs text-slate-700 dark:text-slate-300">
                                  {revision.type === 'name' && 'Flow name changed'}
                                  {revision.type === 'description' && 'Description changed'}
                                  {revision.type === 'missingStepRemoved' && 'Suggested step removed'}
                                  {revision.type === 'missingStepAdded' && 'New step added'}
                                  {revision.type === 'stepLabelChanged' && 'Step label updated'}
                                </div>
                                {revision.original && (
                                  <div className="text-xs text-red-500 dark:text-red-400 line-through mt-0.5">
                                    {revision.original}
                                  </div>
                                )}
                                {revision.revised && (
                                  <div className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                                    {revision.revised}
                                  </div>
                                )}
                                <div className="text-[10px] text-slate-500 dark:text-slate-500 mt-1">
                                  {revision.reason}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Issue #424: AI Debug Info (Prompt and Response) */}
              {aiDebugInfo && (
                <div className="border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowAiDebug(!showAiDebug)}
                    className="w-full px-4 py-2 flex items-center justify-between bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">🔧</span>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        AI Debug Info
                      </span>
                      <span className="text-xs text-slate-400">
                        {aiDebugInfo.tokensUsed && `${aiDebugInfo.tokensUsed} tokens`}
                        {aiDebugInfo.durationMs && ` • ${(aiDebugInfo.durationMs / 1000).toFixed(1)}s`}
                        {aiDebugInfo.cached && ' • cached'}
                      </span>
                    </div>
                    <span className={`text-slate-400 transition-transform ${showAiDebug ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  {showAiDebug && (
                    <div className="p-4 space-y-4 max-h-80 overflow-auto">
                      {/* Prompt Sent */}
                      {aiDebugInfo.promptSent && (
                        <div>
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                            Prompt Sent to AI:
                          </div>
                          <pre className="text-xs bg-slate-100 dark:bg-slate-800 p-3 rounded overflow-auto max-h-48 whitespace-pre-wrap font-mono text-slate-700 dark:text-slate-300">
                            {aiDebugInfo.promptSent}
                          </pre>
                        </div>
                      )}
                      {/* AI Response */}
                      {aiDebugInfo.aiResponse && (
                        <div>
                          <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                            Raw AI Response:
                          </div>
                          <pre className="text-xs bg-slate-100 dark:bg-slate-800 p-3 rounded overflow-auto max-h-48 whitespace-pre-wrap font-mono text-slate-700 dark:text-slate-300">
                            {aiDebugInfo.aiResponse}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Status summary */}
              <div className="flex justify-center gap-4 text-sm">
                <span className="text-green-600">{counts.accepted} accepted</span>
                <span className="text-slate-400">|</span>
                <span className="text-red-600">{counts.rejected} rejected</span>
                <span className="text-slate-400">|</span>
                <span className="text-slate-500">{counts.pending} pending</span>
              </div>
            </div>
          )}

          {/* Applying Phase */}
          {phase === 'applying' && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-full max-w-xs">
                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all duration-300"
                    style={{ width: `${(applyProgress.current / applyProgress.total) * 100}%` }}
                  />
                </div>
                <p className="text-center text-sm text-slate-500 mt-2">
                  Applying {counts.accepted} enhancement{counts.accepted !== 1 ? 's' : ''}...
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200 dark:border-slate-700">
          {phase === 'configure' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={startEnhancement}
                className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <span>✨</span>
                Enhance Flow
              </button>
            </>
          )}

          {phase === 'review' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={applyEnhancements}
                disabled={counts.accepted === 0}
                className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Apply {counts.accepted} Change{counts.accepted !== 1 ? 's' : ''}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Issue #417: Prompt Editor Modal */}
      {promptPreview && (
        <PromptEditorModal
          isOpen={showPromptEditor}
          onClose={() => setShowPromptEditor(false)}
          systemPrompt={promptPreview.systemPrompt}
          guidelines={promptPreview.guidelines}
          customGuidelines={customGuidelines}
          fullPrompt={promptPreview.fullPrompt}
          onSave={handlePromptSave}
        />
      )}
    </div>
  );
}
